import { learnModePresets } from "../constants/learnModePresets";
import { decimalCount } from '../utilities'

export function calculateLearnGain(barIndex, learnData) {
  const MAX_GAIN = 2.27;

  let {
    lowTargetVal,
    highTargetVal,
    records
  } = learnData

 	const rise = highTargetVal - lowTargetVal;

  let highRecordedVal = averageArray(records[barIndex].hard),
      lowRecordedVal = averageArray(records[barIndex].soft)

  if(window.learnLog) {
    console.log('>> bar ' + barIndex + ' soft strikes', records[barIndex].soft)
    console.log('>> bar ' + barIndex + ' hard strikes', records[barIndex].hard)
  }

  let run = highRecordedVal - lowRecordedVal;

	return Math.min(MAX_GAIN, rise/run); // MAX_GAIN = 2.27
}

export function calculateNegativeOffset(barIndex, learnData) {
  const MAX_OFFSET = 255

  let {
    lowTargetVal,
    records,
    tares,
    learnedGains
  } = learnData

	let b;

  let lowRecordedVal = averageArray(records[barIndex].soft)

	b = lowTargetVal - parseFloat(learnedGains[barIndex]) * (lowRecordedVal - tares[barIndex]);

	let calculatedNegativeOffset = Math.round(-b, 0); // deal with every in positives
	if (calculatedNegativeOffset < 0 || calculatedNegativeOffset === -0) {
		calculatedNegativeOffset = 0;
	}

	return Math.min(MAX_OFFSET, calculatedNegativeOffset); // MAX_OFFSET = 255
}

export function averageArray(arr) {
  return arr.reduce((a,b) => a + b, 0) / arr.length
}

export function clamp(num, min, max) {
  return num <= min ? min : num >= max ? max : num;
}

export function isValidRecord(strikes, type='soft', threshold=50) {
  // INFO: possible to have different thresholds for strike types
  // check record length
  if(strikes.length !== 5) return false
  
  const learnThreshold = window.learnThreshold
    ? window.learnThreshold
    : threshold;
  // test if strikes are within threshold of each other
  return Math.max(...strikes) - Math.min(...strikes) < learnThreshold;
}

export function mergeTypedArrays(arr1, arr2, atIndex=0, length=43) {
  return arr1.reduce((merged, b, i) => {
    if(i >= atIndex) {
      merged[i] = arr2[i]
    } else {
      merged[i] = b;
    }

    return merged
  }, new Uint8Array(length))
}


export function findNextLearnIndex(selectedLearnBar, completedStrikes) {
  // console.log(`selectedLearnBar ${selectedLearnBar}, completedStrikes ${completedStrikes[selectedLearnBar]}`);
  let nextBarIndex = (selectedLearnBar + 1 > 42) ? 0 : selectedLearnBar + 1,
      nextStrikeType = false;

  // fresh bars
  if (completedStrikes[nextBarIndex] === 0) {
    return [nextBarIndex, nextStrikeType];
  }

  // if next record isn't blank check it
  nextBarIndex = completedStrikes.findIndex((status, index) => {
    // start at the current bar index
    if(index < nextBarIndex) {
      return false
    }

    switch (status) {
      case 0:
        // no record
        nextStrikeType = false;
        // console.log('case 0');
        return true;
      case 1:
        // soft complete only;
        nextStrikeType = "hard";
        // set striketype
        // console.log("case 1");
        return true;
      case 2:
        // hard complete only;
        nextStrikeType = "soft";
        // set striketype
        // console.log("case 2");
        return true;
      case 3:
        // complete;
        nextStrikeType = false;
        // console.log("case 3");
        return false;

      default:
        nextStrikeType = false;
        // console.log("case default");
        return false;
    }
  });

  return [nextBarIndex, nextStrikeType];
}

export async function matchSensitivityPreset(state) {

  // await new Promise(resolve => setTimeout(resolve, 1000))

  return new Promise((resolve) => {
    const presetGain = state.currentPreset.gain
    const globalThreshold = state.device.globalThreshold
    const crosstalkThreshold = state.device.crosstalkThreshold

    // console.log('>> learnModePresets',learnModePresets)

    // console.log('g', parseFloat(presetGain), 't', globalThreshold, 'c', crosstalkThreshold)

    // find preset matches
    // this is most likely to be outside of preset
    const matchedGain = learnModePresets.find(obj => obj.sensitivity === parseFloat(presetGain))

    // resolution can be either .x or .xx
    // needs to be rounded because of the way OSC returns floats; osc --> 0.8925 when the stored value is 0.9
    const matchedGlobalThreshold = learnModePresets.find(obj => {
      let threshold = typeof globalThreshold === 'string' ? parseFloat(globalThreshold) : parseFloat((globalThreshold).toFixed(2))
      let testValue = null

      switch (decimalCount(threshold)) {
        case 0: // x
          testValue = threshold
          break;
        case 1: // x.x
          testValue = Math.round(threshold * 10) / 10
          break;
        case 2: // x.xx
          testValue = Math.round(threshold * 100) / 100
          break;
      
        default: //x
          testValue = threshold
          break;
      }

      // check for osc values

      testValue = (globalThreshold === 1.09375) ? 1.1 : testValue

      return obj.threshold === testValue
    })

    let matchedCrosstalkThreshold = learnModePresets.filter(obj => obj.crosstalkThreshold === crosstalkThreshold)
    
    if(matchedCrosstalkThreshold.length > 1 ) {
      // compare name to other matched param name
      matchedCrosstalkThreshold = matchedCrosstalkThreshold.find(p => {
        return p.name === matchedGain?.name && matchedGlobalThreshold?.name
      })
    } else {
      // since filter returns an array, get the first item
      matchedCrosstalkThreshold = matchedCrosstalkThreshold[0];
      
    }

    // no match for at least one preset value
    if([matchedGain,matchedGlobalThreshold,matchedCrosstalkThreshold].some(v => v === undefined)) {
      // console.log('some undefined ==> "---"', 'g',matchedGain,'gt',matchedGlobalThreshold,'ctt',matchedCrosstalkThreshold)
      resolve('---')
    }

    // console.log('check', 'mg',matchedGain.name, 'mgt',matchedGlobalThreshold.name, 'mctt',matchedCrosstalkThreshold.name)
    // if there are matches for every param in a preset check if they're all the same and return the preset name
    if([matchedGain.name, matchedGlobalThreshold.name, matchedCrosstalkThreshold.name].every(n => n === matchedGain.name)) {
      resolve(matchedGain.name)
    } else {
      // if there are matches but mixed preset name matches; return "---"
      resolve('---')
    }
  })
}

export function addPathText(p, index, fontSize = '48', fill = '#333') {
  const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
  const b = p.getBBox();
  const humanIndex = index + 1
  const xOffset = (humanIndex > 9) ? 28 : 10

  t.setAttribute("transform", "translate(" + ((b.x + b.width/2) - xOffset) + " " + ((b.y + b.height) - 20) + ")");
  t.textContent = humanIndex;
  t.setAttribute("class", 'bar-index-text');
  t.setAttribute("data-bar-text", `${index}`);
  t.setAttribute("fill", fill);
  t.setAttribute("font-size", fontSize);

  p.parentNode.insertBefore(t, p.nextSibling);
}

// add these and then hide them
export function addCompleteIcon(p, index, type = 'soft') {
  // <circle cx="15" cy="15" r="15" fill="#aeaeae" />
  // <text x="50%" y="50%" text-anchor="middle" fill="white" font-size="10px" font-family="Lato" dy=".3em">S</text>

  const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
  const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  const b = p.getBBox();
  const radius = 20
  const text = type === 'soft' ? 'S' : 'H'
  // const xOffsetText = (radius/2) - (type === 'soft' ? 2 : -1)
  const yOffsetCircle = type === 'soft' ? 40 : 40 + radius + 25
  const yOffsetText = type === 'soft' ? 51 : 51 + radius + 25
  const textFill = type === 'soft' ? '#fff' : '#fff'
  const circleFill = type === 'soft' ? '#04aef0' : '#9a6aad'
  const fontSize = type === 'soft' ? 30 : 29

  c.setAttribute("transform", "translate(" + (b.x + b.width/2) + " " + (b.y + yOffsetCircle) + ")");
  c.setAttribute("r", radius);
  c.setAttribute("class", `bar-circle-${type}`);
  c.setAttribute("data-bar-strikes-complete", false);
  c.setAttribute("data-bar-circle", `${index}`);
  c.setAttribute("data-bar-circle-strike", `${type}`);
  c.setAttribute("fill", circleFill);

  t.setAttribute("transform", "translate(" + (b.x + b.width/2) + " " + (b.y + yOffsetText) + ")");
  t.textContent = text;
  t.setAttribute("class", 'strike-complete-text');
  t.setAttribute("data-bar-strikes-complete", false);
  t.setAttribute("data-bar-strike-type", `${index}`);
  t.setAttribute("data-bar-strike", `${type}`);
  t.setAttribute("text-anchor", "middle");
  t.setAttribute("fill", textFill);
  t.setAttribute("font-size", fontSize);

  p.parentNode.insertBefore(t, p.nextSibling);
  p.parentNode.insertBefore(c, p.nextSibling);
}

export function exportBarData(data) {
	const a = document.createElement('a')
  const timeStampedData = Object.assign(data, {date: new Date()})

	a.setAttribute('href', 'data:text/json;charset=utf8,' + encodeURIComponent(JSON.stringify(timeStampedData, null, 2)));
	a.setAttribute('download', 'malletStation-bar-data.json');
	a.click()
}
